/**
 * Canonical module icons — single source of truth (iter 122.icon-audit).
 *
 * Every module in the app has exactly ONE approved Lucide icon. Page
 * headers, the AppShell side / bottom nav, and Dashboard widgets that
 * preview a module all import from this map so that tapping the
 * widget feels like a visually seamless transition into the module
 * itself.
 *
 * If a screen needs a different icon for a sub-affordance (e.g. a
 * "HandCoins" badge inside an Expense row to indicate a payment
 * event) that's still fine — this file governs ONLY the module-
 * level identity icon.
 *
 * To add a new module: add a key to ``MODULE_ICONS`` here, then have
 * the page header, the navModules entry, and any dashboard widget
 * read from this map. Never introduce a second icon for an existing
 * module.
 */
import {
  Wallet,            // Expenses — incoming/outgoing money for the household
  HandCoins,         // Maintenance — recurring child-support payments
  MessageSquare,     // Messages
  CalendarDays,      // Calendar
  ShieldCheck,       // Contact Visits — evidence-grade record
  FolderLock,        // Documents — securely shared files
  Inbox,             // Requests — incoming / outgoing decisions to make
  BookOpen,          // Journal — private notes
  Users,             // Children — household roster
  Settings,          // Settings
  // The icons below are NOT module identities, only re-exported here
  // so consumers don't need a second lucide import for the common
  // dashboard-summary icons.
  GraduationCap,     // school summary card
  Stethoscope,       // medical summary card
} from "lucide-react";

export const MODULE_ICONS = {
  expenses:       Wallet,
  maintenance:    HandCoins,
  messages:       MessageSquare,
  calendar:       CalendarDays,
  contact_visits: ShieldCheck,
  documents:      FolderLock,
  requests:       Inbox,
  journal:        BookOpen,
  children:       Users,
  settings:       Settings,
};

/** Return the canonical icon for a module slug, with a calm fallback. */
export function iconForModule(slug) {
  return MODULE_ICONS[slug] || MessageSquare;
}

// Re-export for convenience so screens importing from this file get
// the icons that pair with module identity in one place.
export {
  Wallet, HandCoins, MessageSquare, CalendarDays, ShieldCheck,
  FolderLock, Inbox, BookOpen, Users, Settings,
  GraduationCap, Stethoscope,
};

export default MODULE_ICONS;
